import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './FMRPDataTable.css';
import { serviceRequest } from '../shared/xomics-webserver-client';

const SERVICE_CODE = 'familyrisk';

const FMRPDataTable = ({ apiKey, data, title, job, output, pedigreeFiles = [] }) => {
  const [globalSearch, setGlobalSearch] = useState('');
  const [columnSearches, setColumnSearches] = useState({});
  const [displayedRows, setDisplayedRows] = useState(50);
  const incrementAmount = 50;
  const [parsedData, setParsedData] = useState({ headers: [], rows: [] });
  const [visibleColumns, setVisibleColumns] = useState({});
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const columnSelectorRef = useRef(null);

  // Variant include toggle state
  const [includeOverrides, setIncludeOverrides] = useState({});
  const [pendingToggles, setPendingToggles] = useState({});

  const [pedigreeModal, setPedigreeModal] = useState(null); // { blobUrl, label }

  const jobId = job?.job_uuid || '';
  const uploadId = job?.upload_uuid || '';
  const analysisType = job?.analysis_type || '';
  const fileName = output?.name || '';
  const outputId = Number(output?.outputId || 0);
  const reviewPath = uploadId && jobId && outputId > 0
    ? `/uploads/${encodeURIComponent(uploadId)}`
      + `/jobs/${encodeURIComponent(jobId)}`
      + `/outputs/${encodeURIComponent(outputId)}/variant-selections`
    : null;

  // Parse TSV data
  useEffect(() => {
    if (!data || typeof data !== 'string') {
      setParsedData({ headers: [], rows: [] });
      setVisibleColumns({});
      return;
    }

    const lines = data.trim().split('\n');
    if (lines.length === 0) {
      setParsedData({ headers: [], rows: [] });
      setVisibleColumns({});
      return;
    }

    const headers = lines[0].split('\t');
    const rows = lines.slice(1).map(line => {
      const cells = line.split('\t');
      while (cells.length < headers.length) {
        cells.push('');
      }
      return cells;
    });

    setParsedData({ headers, rows });

    const initialVisibility = {};
    headers.forEach((_, index) => {
      initialVisibility[index] = true;
    });
    setVisibleColumns(initialVisibility);
    setColumnSearches({});
    setDisplayedRows(50);
    setIncludeOverrides({});
    setPendingToggles({});
  }, [data]);

  // Detect HGVSc, variant_info, and include columns
  const hgvscIndex = useMemo(() => {
    return parsedData.headers.findIndex(h => h.trim() === 'HGVSc');
  }, [parsedData.headers]);

  const variantInfoIndex = useMemo(() => {
    return parsedData.headers.findIndex(h => h.trim() === 'variant_info');
  }, [parsedData.headers]);

  const includeColumnIndex = useMemo(() => {
    return parsedData.headers.findIndex(h => h.trim() === 'include');
  }, [parsedData.headers]);

  // A file is a variant file if it has HGVSc
  const hasVariantToggle = hgvscIndex !== -1 && Boolean(reviewPath);

  // Pedigree column: show only for family analysis types with HGVSc
  const isFamily = (analysisType || '').includes('family');
  const hasPedigreeColumn = isFamily && hgvscIndex !== -1 && pedigreeFiles.length > 0;

  // Review decisions are stored separately from immutable pipeline outputs.
  useEffect(() => {
    if (!hasVariantToggle) return undefined;
    let active = true;
    const fetchSelections = async () => {
      try {
        const result = await serviceRequest(SERVICE_CODE, apiKey, reviewPath);
        if (!active) return;
        setIncludeOverrides(Object.fromEntries(
          (result.selections || []).map((selection) => [selection.variant, selection.include]),
        ));
      } catch (err) {
        console.error('[VariantInclude] Failed to load review state:', err);
      }
    };
    void fetchSelections();
    return () => { active = false; };
  }, [apiKey, hasVariantToggle, reviewPath]);

  // Primary files default ON, non-primary default OFF
  const isPrimary = useMemo(() => {
    const PRIMARY_FILES = new Set([
      'monogenic_positive_results.txt',
      'carrier_status_results.txt',
      'carrier_screening_result_single.txt',
    ]);
    return PRIMARY_FILES.has((fileName || '').split('/').pop());
  }, [fileName]);

  // Pick the best available variant identifier for the API call
  const getVariantIdentifier = useCallback((row) => {
    if (variantInfoIndex !== -1 && row[variantInfoIndex]) return row[variantInfoIndex];
    if (hgvscIndex !== -1 && row[hgvscIndex]) return row[hgvscIndex];
    return null;
  }, [variantInfoIndex, hgvscIndex]);

  // Get include value for a row
  const getIncludeValue = useCallback((row) => {
    const variant = getVariantIdentifier(row);
    if (variant && includeOverrides[variant] !== undefined) return includeOverrides[variant];
    if (includeColumnIndex !== -1) {
      const val = (row[includeColumnIndex] || '').trim();
      return val !== '0';
    }
    // No include column yet — primary defaults ON, non-primary defaults OFF
    return isPrimary;
  }, [getVariantIdentifier, includeColumnIndex, includeOverrides, isPrimary]);

  // Handle toggle
  const handleToggleInclude = useCallback(async (row) => {
    if (!hasVariantToggle) return;
    const variant = getVariantIdentifier(row);
    if (!variant) return;

    const currentValue = getIncludeValue(row);
    const newValue = !currentValue;

    setIncludeOverrides(prev => ({ ...prev, [variant]: newValue }));
    setPendingToggles(prev => ({ ...prev, [variant]: true }));

    try {
      await serviceRequest(SERVICE_CODE, apiKey, reviewPath, {
        method: 'POST',
        body: JSON.stringify({
          variant,
          include: newValue,
        }),
      });
    } catch (err) {
      console.error('[VariantInclude] Toggle error:', err);
      setIncludeOverrides(prev => ({ ...prev, [variant]: currentValue }));
    } finally {
      setPendingToggles(prev => {
        const next = { ...prev };
        delete next[variant];
        return next;
      });
    }
  }, [apiKey, getIncludeValue, getVariantIdentifier, hasVariantToggle, reviewPath]);

  // Pedigree helpers
  const getRowVariantId = useCallback((row) => {
    if (variantInfoIndex !== -1 && row[variantInfoIndex]) return row[variantInfoIndex];
    return null;
  }, [variantInfoIndex]);

  const getPedigreeFile = useCallback((row) => {
    const candidates = [getRowVariantId(row), hgvscIndex === -1 ? null : row[hgvscIndex]]
      .filter(Boolean)
      .map((value) => String(value).replace(/[^a-zA-Z0-9_.-]/g, '_').toLowerCase());
    for (const file of pedigreeFiles) {
      const basename = String(file.name || '').split('/').pop().toLowerCase();
      if (candidates.some((candidate) => basename === `${candidate}_pedigree_plot.png`
          || basename.endsWith(`_${candidate}_pedigree_plot.png`))) {
        return file;
      }
    }
    return null;
  }, [getRowVariantId, hgvscIndex, pedigreeFiles]);

  const rowHasPedigree = useCallback((row) => {
    return Boolean(getPedigreeFile(row));
  }, [getPedigreeFile]);

  const openPedigreeModal = useCallback(async (row) => {
    const file = getPedigreeFile(row);
    if (!file) return;
    const label = getVariantIdentifier(row) || file.name;
    setPedigreeModal({ blobUrl: null, label }); // show modal with spinner

    try {
      const signed = await serviceRequest(
        SERVICE_CODE,
        apiKey,
        `/uploads/${encodeURIComponent(uploadId)}`
          + `/jobs/${encodeURIComponent(jobId)}`
          + `/outputs/${encodeURIComponent(file.outputId)}/download`,
      );
      const resp = await fetch(signed.downloadUrl);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPedigreeModal(prev => prev ? { ...prev, blobUrl } : null);
    } catch (err) {
      console.error('[Pedigree] Failed to load image:', err);
      setPedigreeModal(null);
    }
  }, [apiKey, getPedigreeFile, getVariantIdentifier, jobId, uploadId]);

  // Cleanup blob URL on modal close
  const closePedigreeModal = useCallback(() => {
    if (pedigreeModal?.blobUrl) URL.revokeObjectURL(pedigreeModal.blobUrl);
    setPedigreeModal(null);
  }, [pedigreeModal]);

  // Hide the raw "include" column from display when we show the toggle
  const visibleColumnIndices = useMemo(() => {
    return Object.entries(visibleColumns)
      .filter(([_, isVisible]) => isVisible)
      .map(([index]) => parseInt(index))
      .filter(index => !(hasVariantToggle && index === includeColumnIndex));
  }, [visibleColumns, hasVariantToggle, includeColumnIndex]);

  const filteredData = useMemo(() => {
    if (!parsedData.rows.length) return [];

    return parsedData.rows.filter(row => {
      const globalMatch = globalSearch === '' ||
        visibleColumnIndices.some(colIndex => {
          const cell = row[colIndex] || '';
          return cell.toLowerCase().includes(globalSearch.toLowerCase());
        });

      if (!globalMatch) return false;

      const columnMatch = Object.entries(columnSearches).every(([colIndex, searchTerm]) => {
        if (!searchTerm) return true;
        const cellValue = row[parseInt(colIndex)] || '';
        return cellValue.toLowerCase().includes(searchTerm.toLowerCase());
      });

      return columnMatch;
    });
  }, [parsedData.rows, globalSearch, columnSearches, visibleColumnIndices]);

  const displayData = useMemo(() => {
    return filteredData.slice(0, displayedRows);
  }, [filteredData, displayedRows]);

  const columnWidths = useMemo(() => {
    const visibleCount = visibleColumnIndices.length;
    if (visibleCount === 0) return [];
    if (visibleCount <= 3) return Array(visibleCount).fill(33);
    if (visibleCount <= 6) return Array(visibleCount).fill(16.66);
    if (visibleCount <= 10) return Array(visibleCount).fill(10);
    return Array(visibleCount).fill(8);
  }, [visibleColumnIndices]);

  const handleColumnSearchChange = (columnIndex, value) => {
    setColumnSearches(prev => ({ ...prev, [columnIndex]: value }));
    setDisplayedRows(50);
  };

  const handleGlobalSearchChange = (value) => {
    setGlobalSearch(value);
    setDisplayedRows(50);
  };

  const toggleColumnVisibility = (columnIndex) => {
    setVisibleColumns(prev => ({ ...prev, [columnIndex]: !prev[columnIndex] }));
    setDisplayedRows(50);
  };

  const toggleAllColumns = (visible) => {
    const newVisibility = {};
    parsedData.headers.forEach((_, index) => {
      newVisibility[index] = visible;
    });
    setVisibleColumns(newVisibility);
    setDisplayedRows(50);
  };

  // Click outside to close column selector
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setShowColumnSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const totalItems = filteredData.length;
  const hasMore = displayedRows < totalItems;

  // Infinite scroll
  const tableWrapperRef = useRef(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    const tableWrapper = tableWrapperRef.current;
    if (!tableWrapper) return;

    const handleScroll = () => {
      if (loadingMoreRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = tableWrapper;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceFromBottom < 200 && hasMore) {
        loadingMoreRef.current = true;
        setDisplayedRows(prev => Math.min(prev + incrementAmount, totalItems));
        setTimeout(() => { loadingMoreRef.current = false; }, 200);
      }
    };

    tableWrapper.addEventListener('scroll', handleScroll, { passive: true });
    return () => tableWrapper.removeEventListener('scroll', handleScroll);
  }, [hasMore, totalItems, incrementAmount]);

  // CSV export
  const downloadCSV = () => {
    const headers = visibleColumnIndices.map(i => parsedData.headers[i]);
    const csvRows = [headers.join(',')];
    filteredData.forEach(row => {
      const values = visibleColumnIndices.map(i => `"${(row[i] || '').replace(/"/g, '""')}"`);
      csvRows.push(values.join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'export'}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (!parsedData.headers.length) {
    return (
      <div className="FMRP-dt-empty">
        <p>No data to display</p>
      </div>
    );
  }

  return (
    <div className="FMRP-dt-container">
      {/* Header */}
      <div className="FMRP-dt-header">
        <div className="FMRP-dt-title-section">
          <h3 className="FMRP-dt-title">{title}</h3>
        </div>

        <div className="FMRP-dt-controls">
          {/* Column Selector */}
          <div className="FMRP-dt-col-selector" ref={columnSelectorRef}>
            <button
              className="FMRP-dt-col-selector-btn"
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              title="Select visible columns"
            >
              <i className="fas fa-columns"></i>
              <span>Columns ({visibleColumnIndices.length}/{parsedData.headers.length})</span>
            </button>

            {showColumnSelector && (
              <div className="FMRP-dt-col-dropdown">
                <div className="FMRP-dt-col-dropdown-header">
                  <span>Select Columns</span>
                  <div className="FMRP-dt-col-dropdown-actions">
                    <button onClick={() => toggleAllColumns(true)} className="FMRP-dt-select-btn">All</button>
                    <button onClick={() => toggleAllColumns(false)} className="FMRP-dt-select-btn">None</button>
                  </div>
                </div>
                <div className="FMRP-dt-col-list">
                  {parsedData.headers.map((header, index) => (
                    <label key={index} className="FMRP-dt-col-item">
                      <input
                        type="checkbox"
                        checked={visibleColumns[index] || false}
                        onChange={() => toggleColumnVisibility(index)}
                      />
                      <span className="FMRP-dt-col-label">{header}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* CSV Export */}
          <button className="FMRP-dt-col-selector-btn" onClick={downloadCSV} title="Export CSV">
            <i className="fas fa-file-csv"></i>
          </button>

          {/* Global Search */}
          <div className="FMRP-dt-global-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search all columns..."
              value={globalSearch}
              onChange={(e) => handleGlobalSearchChange(e.target.value)}
              className="FMRP-dt-global-search-input"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="FMRP-dt-wrapper" ref={tableWrapperRef}>
        <table className="FMRP-dt-table">
          <thead>
            <tr>
              {hasVariantToggle && (
                <th className="FMRP-dt-th FMRP-dt-th-include">
                  <div className="FMRP-dt-col-header">
                    <span className="FMRP-dt-col-title">Include</span>
                  </div>
                </th>
              )}
              {hasPedigreeColumn && (
                <th className="FMRP-dt-th FMRP-dt-th-pedigree">
                  <div className="FMRP-dt-col-header">
                    <span className="FMRP-dt-col-title">Pedigree</span>
                  </div>
                </th>
              )}
              {visibleColumnIndices.map((columnIndex, displayIndex) => (
                <th key={columnIndex} style={{ width: `${columnWidths[displayIndex]}%` }} className="FMRP-dt-th">
                  <div className="FMRP-dt-col-header">
                    <span className="FMRP-dt-col-title">{parsedData.headers[columnIndex]}</span>
                    <input
                      type="text"
                      placeholder="Filter..."
                      value={columnSearches[columnIndex] || ''}
                      onChange={(e) => handleColumnSearchChange(columnIndex, e.target.value)}
                      className="FMRP-dt-col-search"
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, filteredIndex) => {
              const variantIdentifier = getVariantIdentifier(row);
              const included = hasVariantToggle ? getIncludeValue(row) : null;
              const isPending = variantIdentifier ? pendingToggles[variantIdentifier] : false;
              const hasPed = hasPedigreeColumn ? rowHasPedigree(row) : false;

              return (
                <tr key={filteredIndex} className="FMRP-dt-row">
                  {hasVariantToggle && (
                    <td className="FMRP-dt-td FMRP-dt-td-include">
                      <button
                        className={`FMRP-dt-include-toggle ${included ? 'on' : 'off'}${isPending ? ' pending' : ''}`}
                        onClick={() => handleToggleInclude(row)}
                        disabled={isPending}
                        title="Include in clinical report"
                      >
                        <span className="FMRP-dt-include-track">
                          <span className="FMRP-dt-include-thumb" />
                        </span>
                      </button>
                    </td>
                  )}
                  {hasPedigreeColumn && (
                    <td className="FMRP-dt-td FMRP-dt-td-pedigree">
                      {hasPed ? (
                        <button
                          className="FMRP-dt-pedigree-btn has-pedigree"
                          onClick={() => openPedigreeModal(row)}
                          title="View pedigree"
                        >
                          <i className="fas fa-sitemap"></i>
                        </button>
                      ) : null}
                    </td>
                  )}
                  {visibleColumnIndices.map((columnIndex, displayIndex) => (
                    <td key={columnIndex} style={{ width: `${columnWidths[displayIndex]}%` }} className="FMRP-dt-td">
                      <div className="FMRP-dt-cell" title={row[columnIndex]}>
                        {row[columnIndex]}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="FMRP-dt-footer">
        <div className="FMRP-dt-info">
          Showing {Math.min(displayedRows, totalItems)} of {totalItems} rows
          {visibleColumnIndices.length < parsedData.headers.length && (
            <span className="FMRP-dt-hidden-info"> · {parsedData.headers.length - visibleColumnIndices.length} columns hidden</span>
          )}
          {hasMore && <span className="FMRP-dt-scroll-hint"> · Scroll for more...</span>}
        </div>
      </div>

      {/* Pedigree Modal */}
      {pedigreeModal && (
        <div className="FMRP-dt-pedigree-overlay" onClick={closePedigreeModal}>
          <div className="FMRP-dt-pedigree-modal" onClick={(e) => e.stopPropagation()}>
            <div className="FMRP-dt-pedigree-modal-header">
              <span>Pedigree — {pedigreeModal.label}</span>
              <button className="FMRP-dt-pedigree-modal-close" onClick={closePedigreeModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="FMRP-dt-pedigree-modal-body">
              {pedigreeModal.blobUrl ? (
                <img
                  src={pedigreeModal.blobUrl}
                  alt={`Pedigree — ${pedigreeModal.label}`}
                />
              ) : (
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '2vh', color: 'rgba(255,255,255,0.5)' }}></i>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FMRPDataTable;
